import React, { useEffect, useRef } from 'react';
import { useLoadScript } from '@react-google-maps/api';
import usePlacesAutocomplete, { getDetails } from 'use-places-autocomplete';
import {
  BuildingStorefrontIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';
import { TWInput } from '../ui';

const libraries = ['places'];

const RestaurantAutoComplete = (props) => {
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY,
    libraries,
  });

  if (!isLoaded) {
    return (
      <TWInput
        label="Restaurant name"
        labelIcon={BuildingStorefrontIcon}
        size="lg"
        required
        disabled
        placeholder="Loading..."
        value={props.value || ''}
        onChange={() => {}}
      />
    );
  }
  return <PlacesAutoComplete {...props} />;
};

const FOOD_TYPES = new Set([
  'restaurant',
  'food',
  'meal_takeaway',
  'meal_delivery',
  'cafe',
  'bakery',
  'bar',
]);

const PlacesAutoComplete = ({
  value,
  onChange,
  onSelect,
  error,
  disabled,
}) => {
  const wrapRef = useRef(null);

  const {
    ready,
    value: query,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    requestOptions: {
      types: ['establishment'],
      componentRestrictions: { country: ['us'] },
    },
    debounce: 400,
  });

  useEffect(() => {
    if ((value || '') !== query) {
      setValue(value || '', false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        clearSuggestions();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () =>
      document.removeEventListener('mousedown', handleClickOutside);
  }, [clearSuggestions]);

  const handleInput = (e) => {
    setValue(e.target.value);
    onChange(e.target.value);
  };

  const handleSelect = async (placeId, fallbackName) => {
    try {
      const res = await getDetails({
        placeId,
        fields: [
          'name',
          'address_component',
          'geometry',
          'formatted_address',
        ],
      });

      let street = '';
      let city = '';
      let state = '';
      let zipCode = '';
      let latitude = null;
      let longitude = null;

      const keys = {
        streetNumber: 'street_number',
        streetName: 'route',
        city: 'locality',
        state: 'administrative_area_level_1',
        zipCode: 'postal_code',
      };

      (res.address_components || []).forEach((element) => {
        if (element.types.includes(keys.streetNumber))
          street = element.long_name;
        else if (element.types.includes(keys.streetName))
          street += ' ' + element.long_name;
        else if (element.types.includes(keys.city)) city = element.long_name;
        else if (element.types.includes(keys.state))
          state = element.short_name;
        else if (element.types.includes(keys.zipCode))
          zipCode = element.long_name;
      });

      if (res.geometry) {
        latitude = res.geometry.location.lat();
        longitude = res.geometry.location.lng();
      }

      const placeName = res.name || fallbackName;
      setValue(placeName, false);
      clearSuggestions();
      onSelect({
        companyname: placeName,
        street: street.trim(),
        city,
        state,
        zipCode,
        latitude,
        longitude,
      });
    } catch (err) {
      console.error(err);
    }
  };

  // Rank: food-related establishments first
  const sortedData = [...data].sort((a, b) => {
    const aFood = (a.types || []).some((t) => FOOD_TYPES.has(t));
    const bFood = (b.types || []).some((t) => FOOD_TYPES.has(t));
    if (aFood === bFood) return 0;
    return aFood ? -1 : 1;
  });

  const showSuggestions = status === 'OK' && sortedData.length > 0;

  return (
    <div ref={wrapRef} className="relative z-[1000]">
      <TWInput
        label="Restaurant name"
        labelIcon={BuildingStorefrontIcon}
        size="lg"
        required
        placeholder="Search by name to auto-fill the address"
        value={query}
        onChange={handleInput}
        disabled={!ready || disabled}
        error={error}
        autoComplete="off"
      />
      {showSuggestions && (
        <ul className="absolute left-0 right-0 z-[1001] mt-1 max-h-72 overflow-auto rounded-md border border-slate-200 bg-white py-1 shadow-xl ring-1 ring-black/5 dark:border-slate-700 dark:bg-slate-800 dark:ring-white/5">
          {sortedData.map((suggestion) => {
            const {
              place_id,
              structured_formatting: { main_text, secondary_text },
              types = [],
            } = suggestion;
            const isFood = types.some((t) => FOOD_TYPES.has(t));
            return (
              <li
                key={place_id}
                onClick={() => handleSelect(place_id, main_text)}
                className="flex cursor-pointer items-start gap-2.5 px-3 py-2.5 transition hover:bg-blue-50 dark:hover:bg-slate-700/70"
              >
                <span
                  className={
                    'mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full ' +
                    (isFood
                      ? 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300'
                      : 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-300')
                  }
                >
                  <MapPinIcon className="h-3.5 w-3.5" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">
                    {main_text}
                  </div>
                  <div className="truncate text-xs text-slate-500 dark:text-slate-400">
                    {secondary_text}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default RestaurantAutoComplete;
